/**
 * 登录弹窗状态管理
 * 用于在应用任何位置控制登录弹窗的显示和隐藏
 */

import { ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import authState from './authState'

/**
 * @typedef {Object} AuthDialogOptions
 * @property {string} [redirectUrl] - 登录成功后重定向的URL
 * @property {Function} [callback] - 登录成功后的回调函数
 */

/**
 * @typedef {Object} AuthDialogState
 * @property {boolean} visible - 登录弹窗是否可见
 * @property {string} redirectUrl - 登录成功后的重定向路径
 * @property {Function|null} callback - 登录后的回调
 */

// 创建响应式状态
/** @type {AuthDialogState} */
const state = reactive({
  // 登录弹窗是否可见
  visible: false,
  // 登录成功后的重定向路径
  redirectUrl: '',
  // 登录后的回调
  callback: null
})

// 用于追踪上一次请求
const lastShowRequest = ref({
  timestamp: 0,
  handled: true
})

// 使用闭包创建服务实例
const authDialog = {
  /**
   * 显示登录弹窗
   * @param {AuthDialogOptions} options 配置选项
   */
  show(options = {}) {
    console.log('authDialog.show called', options)
    
    // 记录本次请求
    const currentTimestamp = Date.now()
    lastShowRequest.value = {
      timestamp: currentTimestamp,
      handled: false
    }
    
    // 设置状态
    state.redirectUrl = options.redirectUrl || ''
    state.callback = options.callback || null
    
    // 强制重置状态
    if (state.visible) {
      // 如果已经显示，先隐藏再显示以触发变更
      state.visible = false
      
      // 使用setTimeout允许视图更新
      setTimeout(() => {
        if (!lastShowRequest.value.handled && lastShowRequest.value.timestamp === currentTimestamp) {
          state.visible = true
          lastShowRequest.value.handled = true
          console.log('authDialog.show - 强制重置后再显示')
        }
      }, 10)
    } else {
      // 直接显示
      state.visible = true
      lastShowRequest.value.handled = true
      console.log('authDialog.show - 直接显示')
    }
  },
  
  /**
   * 隐藏登录弹窗
   */
  hide() {
    console.log('authDialog.hide called')
    state.visible = false
    state.redirectUrl = ''
    state.callback = null
  },
  
  /**
   * 登录成功处理
   * @param {Object} result 登录结果
   */
  onLoginSuccess(result) {
    console.log('authDialog.onLoginSuccess called', result)
    if (state.callback) {
      state.callback(result)
    }
    this.hide()
  },
  
  /**
   * 获取当前状态
   * @returns {AuthDialogState} 当前状态对象
   */
  getState() {
    return state
  },
  
  /**
   * 检查用户是否已登录，如果未登录则显示登录弹窗
   * @param {AuthDialogOptions} options 配置选项
   * @returns {Boolean} 是否已登录
   */
  checkLogin(options = {}) {
    const isLoggedIn = authState.isLoggedIn.value
    if (!isLoggedIn) {
      this.show(options)
    }
    return isLoggedIn
  }
}

export default authDialog 